import { useState } from 'react'
import { useParams, useSearchParams, useNavigate, Link } from 'react-router'
import { Loader2, Trash2, Pencil } from 'lucide-react'
import { formatFullDate, formatAmount } from '@/lib/utils'
import { UNCATEGORIZED_LABEL } from '@/lib/constants'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { TagBadge } from '@/components/tag/tag-badge'
import { useTransaction, useDeleteTransaction } from '@/lib/hooks'


export default function TransactionDetailPage() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const month = searchParams.get('month') || undefined

  const { data: transaction = null, isLoading: loading } = useTransaction(id)

  const deleteTx = useDeleteTransaction()

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleDelete = () => {
    if (deleteTx.isPending) return
    if (!id) return
    deleteTx.mutate(id, {
      onSuccess: () => navigate('/transactions', { replace: true }),
      onSettled: () => setDeleteDialogOpen(false),
    })
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-lg space-y-4 p-4">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-24" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Skeleton className="h-4 w-8" />
                <Skeleton className="h-5 w-24" />
              </div>
              <div className="space-y-1">
                <Skeleton className="h-4 w-12" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
            <div className="space-y-2">
              <Skeleton className="h-4 w-16" />
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg border px-3 py-2">
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                  <Skeleton className="h-4 w-14" />
                </div>
              ))}
            </div>
            <div className="rounded-lg bg-muted p-3 text-center">
              <Skeleton className="mx-auto h-4 w-16" />
              <Skeleton className="mx-auto mt-1 h-7 w-24" />
            </div>
            <div className="flex gap-2 pt-2">
              <Skeleton className="h-9 flex-1" />
              <Skeleton className="h-9 flex-1" />
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!transaction) {
    return (
      <div className="mx-auto max-w-lg p-4">
        <p className="py-16 text-center text-sm text-muted-foreground">
          取引が見つかりませんでした
        </p>
      </div>
    )
  }

  const displayDate = formatFullDate(transaction.date)
  const editPath = month
    ? `/transactions/${id}/edit?month=${month}`
    : `/transactions/${id}/edit`

  return (
    <div className="mx-auto max-w-lg space-y-4 p-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>取引詳細</CardTitle>
            <div className="flex items-center gap-2">
              {transaction.input_method === 'recurring' && (
                <Badge variant="secondary">固定</Badge>
              )}
              {transaction.scope === 'personal' && (
                <Badge variant="outline">個人</Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">日付</p>
              <p className="font-medium">{displayDate}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">支払者</p>
              <div className="flex items-center gap-1.5">
                <Avatar className="size-5 shrink-0">
                  {transaction.paid_by_avatar_url && (
                    <AvatarImage src={transaction.paid_by_avatar_url} alt={transaction.paid_by_name ?? ''} />
                  )}
                  <AvatarFallback className="text-[10px]">
                    {transaction.paid_by_name?.charAt(0) ?? '?'}
                  </AvatarFallback>
                </Avatar>
                <p className="font-medium">{transaction.paid_by_name || '-'}</p>
              </div>
            </div>
            {transaction.store_name && (
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">店名</p>
                <p className="font-medium">{transaction.store_name}</p>
              </div>
            )}
            {transaction.memo && (
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">メモ</p>
                <p className="whitespace-pre-wrap text-sm">{transaction.memo}</p>
              </div>
            )}
            {transaction.tags.length > 0 && (
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">タグ</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {transaction.tags.map(tag => (
                    <TagBadge key={tag.id} name={tag.name} color={tag.color} to={`/tags/${tag.id}`} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">品目明細</p>
            <div className="space-y-2">
              {transaction.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-lg border px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{item.name}</p>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      {item.parent_category_color ? (
                        <span
                          className="inline-block size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: item.parent_category_color }}
                        />
                      ) : (
                        <span className="inline-block size-2 shrink-0 rounded-full border border-muted-foreground" />
                      )}
                      {item.parent_category_name && item.category_name
                        ? `${item.parent_category_name} > ${item.category_name}`
                        : item.category_name || UNCATEGORIZED_LABEL}
                    </span>
                  </div>
                  <span className="shrink-0 text-sm font-medium">
                    {formatAmount(item.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg bg-muted p-3 text-center">
            <span className="text-sm text-muted-foreground">合計金額</span>
            <p className="text-2xl font-bold">
              {formatAmount(transaction.total_amount)}
            </p>
            {transaction.tax_amount > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                (うち外税 {formatAmount(transaction.tax_amount)})
              </p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              asChild
            >
              <Link to={editPath}>
                <Pencil className="size-4" />
                編集
              </Link>
            </Button>
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
              <DialogTrigger asChild>
                <Button type="button" variant="destructive" className="flex-1">
                  <Trash2 className="size-4" />
                  削除
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>取引を削除しますか？</DialogTitle>
                  <DialogDescription>
                    この操作は取り消せません。取引と品目明細が削除されます。
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(false)}
                  >
                    キャンセル
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleteTx.isPending}
                  >
                    {deleteTx.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
                    削除する
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
